package no.vareflow.web

import no.vareflow.app.domain.*

// Eier-siden → "Besøk på vareflow.no": the anonymous day counters (see VisitStats).

private val visitState = object {
    var days: List<VisitDay>? = null
    var loading = false
    var error: String? = null
    /** Shown period in days: 7 or 30 (90 days are loaded). */
    var range = 30
}

private const val LOADED_DAYS = 90

fun WebApp.loadOwnerVisits() {
    if (visitState.loading) return
    visitState.loading = true
    visitState.error = null
    cloud.loadVisitDays(LOADED_DAYS) { result ->
        visitState.loading = false
        result.onSuccess { visitState.days = it }.onFailure { visitState.error = it.message }
        requestRender()
    }
}

private fun formatShortDate(date: String): String {
    // "2026-10-13" → "13.10."
    val parts = date.split('-')
    return if (parts.size == 3) "${parts[2]}.${parts[1]}." else date
}

private fun percent(part: Int, whole: Int): String = if (whole <= 0) "0 %" else "${(part * 100.0 / whole).let { kotlin.math.round(it).toInt() }} %"

fun WebApp.renderOwnerVisits(root: El) {
    val s = visitState
    root.div("section-row visit-head") {
        h2(t("Besøk på vareflow.no", "Visits to vareflow.no"), "section-title")
        div("chip-row visit-range") {
            chip(t("7 dager", "7 days"), s.range == 7) { s.range = 7; requestRender() }
            chip(t("30 dager", "30 days"), s.range == 30) { s.range = 30; requestRender() }
            button("↻", "icon-btn outline") { loadOwnerVisits() }.attr("aria-label", t("Oppdater", "Refresh"))
        }
    }
    s.error?.let { root.p(it, "error-text") }
    val all = s.days
    if (all == null) {
        root.p(t("Henter besøk …", "Loading visits …"), "muted small")
        return
    }
    val shown = all.takeLast(s.range)
    val today = all.lastOrNull()
    val totals = VisitStats.totals(shown)
    root.div("metric-row owner-metrics") {
        metricCard(t("I dag", "Today"), (today?.pageViews ?: 0).toString())
        metricCard(t("Besøk, ${s.range} dager", "Visits, ${s.range} days"), totals.pageViews.toString())
        metricCard(t("Innlogginger, ${s.range} dager", "Logins, ${s.range} days"), totals.logins.toString())
    }
    root.div("card visit-card") {
        if (totals.pageViews == 0 && totals.logins == 0) {
            p(t("Ingen besøk i perioden ennå.", "No visits in this period yet."), "muted small")
        }
        // Day bars: visits per day, the newest at the right.
        val max = shown.maxOfOrNull { it.pageViews }?.coerceAtLeast(1) ?: 1
        div("visit-bars") {
            attr("role", "img")
            attr("aria-label", t("Besøk per dag", "Visits per day"))
            shown.forEach { day ->
                div("visit-bar-slot") {
                    attr("title", "${formatShortDate(day.date)}: ${day.pageViews} " + t("besøk", "visits") + ", ${day.logins} " + t("innlogginger", "logins"))
                    div("visit-bar" + if (day.pageViews == 0) " zero" else "") {
                        node.style.height = "${(day.pageViews * 100.0 / max).coerceAtLeast(if (day.pageViews > 0) 4.0 else 0.0)}%"
                    }
                }
            }
        }
        div("visit-axis") {
            span("", shown.firstOrNull()?.date?.let { formatShortDate(it) } ?: "")
            span("", t("maks $max per dag", "max $max a day"))
            span("", t("i dag", "today"))
        }

        // Countries
        if (totals.countries.isNotEmpty()) {
            div("visit-sub", t("Land", "Countries"))
            val top = totals.countries.first().second.coerceAtLeast(1)
            totals.countries.take(12).forEach { (code, count) ->
                div("visit-row") {
                    span("visit-name", VisitStats.countryName(code))
                    div("visit-track") { div("visit-fill") { node.style.width = "${count * 100.0 / top}%" } }
                    span("visit-num", "$count · ${percent(count, totals.pageViews)}")
                }
            }
            if (totals.countries.size > 12) p(t("+ ${totals.countries.size - 12} andre land", "+ ${totals.countries.size - 12} other countries"), "muted tiny")
        }

        // Devices
        val mobile = totals.devices["mobile"] ?: 0
        val desktop = totals.devices["desktop"] ?: 0
        if (mobile + desktop > 0) {
            div("visit-sub", t("Enhet", "Device"))
            div("visit-devices",
                t("Mobil", "Mobile") + " $mobile (${percent(mobile, mobile + desktop)}) · " +
                    t("PC", "Computer") + " $desktop (${percent(desktop, mobile + desktop)})")
        }
    }
    root.p(t("Teller sidevisninger per nettleserfane (en ny innlasting telles ikke på nytt) og vellykkede innlogginger på nettet. " +
        "Ingen IP-adresse, navn eller informasjonskapsler lagres. Landet anslås fra nettleserens tidssone. Dine egne besøk telles ikke.",
        "Counts page views per browser tab (a reload is not counted again) and successful logins on the web. " +
        "No IP address, name or cookies are stored. The country is estimated from the browser's time zone. Your own visits are not counted."), "muted tiny")
}
